import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaUserGraduate } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import StudentCreate from '../../create/js/studentCreate';
import { studentAPI } from '../../../../utils/api';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';

function StudentTable({ onCreateStudent, reloadKey = 0 }) {
  const [studentData, setStudentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await studentAPI.getAll();
      setStudentData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudents(); }, [reloadKey]);

  const handleDelete = async (student) => {
    const res = await Swal.fire({
      title: 'Delete Student?',
      text: `Are you sure you want to delete ${student.student_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonColor: '#e74c3c',
    });
    if (res.isConfirmed) {
      try {
        await studentAPI.delete(student.id);
        await fetchStudents();
        Swal.fire({ title: 'Deleted', text: 'Student record removed', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    }
  };

  const exportCsv = async () => {
    try {
      const res = await studentAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'students_export.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { Swal.fire('Error', err.message, 'error'); }
  };

  const importCsv = async (csv) => {
    const res = await studentAPI.importCsv(csv);
    await fetchStudents();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const res = await studentAPI.downloadSampleCsv();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'students_sample.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'b_form_no', header: 'B-Form No' },
    { 
      accessorKey: 'student_name', 
      header: 'Student Name', 
      cell: ({ getValue }) => <span style={{ fontWeight: 600 }}>{getValue()}</span> 
    },
    { accessorKey: 'father_name', header: "Father's Name" },
    { 
      accessorKey: 'gender', 
      header: 'Gender', 
      cell: ({ getValue }) => getValue() === 'M' ? 'Male' : 'Female' 
    },
    { accessorKey: 'class', header: 'Class' },
    { accessorKey: 'semis_code', header: 'SEMIS Code' },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => { setSelectedStudent(row.original); setShowViewModal(true); }}><FaEye /></button>
          <button className="eduss-global-action-btn edit" onClick={() => { setSelectedStudent(row.original); setShowEditModal(true); }}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = studentData.filter(s => {
    const f = searchTerm.toLowerCase();
    return (!searchTerm || s.student_name?.toLowerCase().includes(f) || s.b_form_no?.includes(f) || s.semis_code?.includes(f))
    && (!filterClass || s.class === filterClass);
  });

  const classes = [...new Set(studentData.map(s => s.class).filter(Boolean))].map(c => ({ label: c, value: c }));

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Student Registry"
        subtitle="Complete database of enrolled students across Sindh educational institutions"
        onAdd={onCreateStudent}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setSearchTerm}
        onFilterChange={(name, value) => setFilterClass(value)}
        filterOptions={[{ name: 'class', value: filterClass, options: classes }]}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>

      {showViewModal && selectedStudent && (
        <div className="eduss-global-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Student Profile</h3>
              <button onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <div style={{ display: 'flex', gap: 20, marginBottom: 25, alignItems: 'center' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(15, 111, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#0f6fad' }}>
                  <FaUserGraduate />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.4rem' }}>{selectedStudent.student_name}</h4>
                  <p style={{ margin: 0, color: '#666' }}>B-Form: {selectedStudent.b_form_no}</p>
                </div>
              </div>
              <table className="mini-info-table">
                <tbody>
                  <tr><th>Father&apos;s Name</th><td>{selectedStudent.father_name}</td></tr>
                  <tr><th>Gender</th><td>{selectedStudent.gender === 'M' ? 'Male' : 'Female'}</td></tr>
                  <tr><th>Date of Birth</th><td>{selectedStudent.date_of_birth || '-'}</td></tr>
                  <tr><th>Class</th><td>{selectedStudent.class}</td></tr>
                  <tr><th>SEMIS Code</th><td>{selectedStudent.semis_code}</td></tr>
                  <tr><th>Religion</th><td>{selectedStudent.religion || '-'}</td></tr>
                  <tr><th>Contact</th><td>{selectedStudent.student_contact || '-'}</td></tr>
                  <tr><th>Disability Status</th><td>{selectedStudent.disability_status || '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedStudent && (
        <div className="eduss-global-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Update Student Record</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <StudentCreate
                student={selectedStudent}
                onClose={() => setShowEditModal(false)}
                onSubmit={async () => { await fetchStudents(); setShowEditModal(false); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

StudentTable.propTypes = {
  onCreateStudent: PropTypes.func,
  reloadKey: PropTypes.number,
};

export default StudentTable;
